import { useCallback, useEffect, useRef, useState } from 'react'
import { MapRef } from 'react-map-gl/maplibre'
import { MapConfig, MapLayer, MapStyle, ObsMarkerData } from '../../meta'
import { District, DistrictLayerConfig, Districts } from '../../meta'
import { DownloadFormat, downloadData, elmIdLookup, getObs, signOut as signOutByApi } from 'src/api'
import { ChartPt, ObsItem, Station } from 'src/models'
import dayjs, { Dayjs } from 'dayjs'
import _, { clamp, debounce, isNil, maxBy, minBy, set, uniqBy } from 'lodash'
import Supercluster from 'supercluster'
import { usePlayer } from 'src/hooks/usePlayer'
import { useNavigate } from 'react-router-dom'
import { download } from 'src/utils'
import { shouldSignIn } from 'src/auth'

type CtrBtnType = 'zoom' | 'zoom-in' | 'zoom-out' | 'direction' | 'locate' | '3D'
export type DownloadRange = 'today' | 'yesterday' | 'last-24h' | 'last-7d' | 'custom'
const supercluster = new Supercluster({ radius: 24, maxZoom: 24 })
const mapMoveDurationMs = 800

const clusterMarkers = (markers: ObsMarkerData[], layer: MapLayer, zoomLevel: number) => {
  // cluster markers only if obs value is valid
  const sc = supercluster.load(
    markers.filter(v => {
      const value = v.properties.obs[layer]
      return !isNil(value) && !isNaN(value)
    }),
  )
  const clusters = sc.getClusters([113, 21, 115, 24], zoomLevel)
  const _markers: ObsMarkerData[] = markers.map(v => ({
    ...v,
    properties: {
      ...v.properties,
      clustered: !clusters.some(({ id }) => id === v.id),
    },
  }))
  return _markers
}

const groupMultiDevices = (markers: ObsMarkerData[], layer: MapLayer) => {
  const stnIds = uniqBy(markers, v => v.id).map(v => v.id)
  const _markers: ObsMarkerData[] = stnIds.map(id => {
    const stnMarkers = markers.filter(v => v.id === id)
    const validStnMarkers = stnMarkers.filter(v => !isNaN(v.properties.obs[layer]))
    const sorted = validStnMarkers.sort((a, b) =>
      b.properties.deviceId.localeCompare(a.properties.deviceId),
    )
    return sorted[0] ?? stnMarkers[0]
  })
  return _markers
}

const mergeMarkers = (
  data: ObsMarkerData[],
  obsItems: ObsItem[],
  stations: Station[],
  layer: MapLayer,
) => {
  const markers = data.concat([])
  const indexes = data.reduce((acc, v, i) => {
    acc[`${v.id}_${v.properties.timestamp}_${v.properties.deviceId}`] = i
    return acc
  }, {} as Record<string, number>)
  let timestamp = Date.now()
  for (let item of obsItems) {
    const { timestamp: tStr, readings } = item
    const timestamp = dayjs(tStr).toDate().getTime()
    for (let { station_id, value, device_id } of readings) {
      // filter for unique station_id and timestamp
      const index = indexes[`${station_id}_${timestamp}_${device_id}`] ?? -1
      if (index === -1) {
        const station = stations.find(v => v.id === station_id)
        if (!station) continue

        markers.push({
          id: station_id,
          geometry: {
            coordinates: [station.location.longitude, station.location.latitude],
            type: 'Point',
          },
          type: 'Feature',
          properties: {
            timestamp,
            obs: { [layer]: parseFloat(value) },
            deviceId: device_id,
          },
        } as ObsMarkerData)
      } else {
        set(markers[index], `properties.obs.${layer}`, parseFloat(value))
      }
    }
  }
  const time_used = Date.now() - timestamp
  console.log('mergeMarkers', time_used)
  return markers
}

const filterMarkersForTime = ({
  markers,
  time,
}: {
  markers: ObsMarkerData[]
  time?: Dayjs
}) => {
  const timestamp = time
    ? time.unix() * 1000
    : maxBy(markers, v => v.properties.timestamp)?.properties?.timestamp ?? Date.now()
  return markers.filter(v => v.properties.timestamp === timestamp)
}

const fillMissingStationMarkers = ({
  markers,
  stations,
  layer,
  time,
}: {
  markers: ObsMarkerData[]
  stations?: Station[]
  layer: MapLayer
  time?: Dayjs
}) => {
  if (!stations?.length) return markers

  const stationIds = new Set(markers.map(v => v.id))
  const timestamp = time
    ? time.unix() * 1000
    : maxBy(markers, v => v.properties.timestamp)?.properties?.timestamp ?? Date.now()

  const placeholders = stations
    .filter(stn => !stationIds.has(stn.id))
    .filter(stn => Number.isFinite(stn.location.latitude) && Number.isFinite(stn.location.longitude))
    .map(stn => ({
      id: stn.id,
      geometry: {
        coordinates: [stn.location.longitude, stn.location.latitude],
        type: 'Point',
      },
      type: 'Feature',
      properties: {
        timestamp,
        obs: { [layer]: Number.NaN },
        deviceId: stn.devices?.[0]?.device_id ?? '',
        clustered: false,
      },
    } as ObsMarkerData))

  return markers.concat(placeholders)
}

const getLayerAvailableTimestamps = (markers: ObsMarkerData[], layer: MapLayer) => {
  const timestamps = new Set<number>()
  for (let marker of markers) {
    const value = marker.properties.obs[layer]
    if (isNaN(value)) continue
    timestamps.add(marker.properties.timestamp)
  }

  return [...timestamps].sort((a, b) => a - b)
}

const resolveTimestampForLayer = ({
  markers,
  layer,
  requestedTime,
}: {
  markers: ObsMarkerData[]
  layer: MapLayer
  requestedTime?: Dayjs
}) => {
  const available = getLayerAvailableTimestamps(markers, layer)
  if (!available.length) {
    return requestedTime
  }

  if (!requestedTime) {
    return dayjs(available[available.length - 1])
  }

  const requestedTs = requestedTime.valueOf()
  let resolved = available[0]
  for (let ts of available) {
    if (ts > requestedTs) break
    resolved = ts
  }

  return dayjs(resolved)
}

const getClosestFiveMin = (time: Dayjs) => {
  const min = time.subtract(4, 'minutes').minute()
  const min5 = Math.floor(min / 5) * 5
  return time.minute(min5).second(0).millisecond(0)
}

const isDistrict = (value?: string | null): value is District => {
  return !!value && Districts.includes(value as District)
}

const flyToSmoothly = ({
  map,
  lat,
  lng,
  zoom,
}: {
  map?: MapRef
  lat?: number
  lng?: number
  zoom?: number
}) => {
  if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return

  map.flyTo({
    center: { lat: lat as number, lng: lng as number },
    zoom,
    duration: mapMoveDurationMs,
    essential: true,
  })
}

type PlaySpeed = 1 | 2 | 0.5
const playSpeeds: PlaySpeed[] = [0.5, 1, 2]
export const useViewModel = () => {
  const navigate = useNavigate()
  const [selectedLayer, setSelectLayer] = useState<MapLayer>('temperature')
  const [zoomLevel, setZoomLevel] = useState<number>(11)
  const [rotateDegree, setRotateDegree] = useState(0)
  const [tiltDegree, setTiltDegree] = useState(0)
  const [hoveringFeature, setHoveringFeature] = useState<string | undefined>()
  const [stations, setStations] = useState<Station[]>()
  const [timeRange, setTimeRange] = useState<[Dayjs, Dayjs] | undefined>()
  const [{ obsMarkersShown, obsMarkers }, setObsMarkers] = useState<{
    obsMarkersShown?: ObsMarkerData[],
    obsMarkers?: ObsMarkerData[]
  }>({})
  const [selectedStation, setSelectedStation] = useState<Station | undefined>()
  const [downloadSelection, setDownloadSelection] = useState<
    | {
      stage: 'range' | 'format'
      range?: DownloadRange
      format?: DownloadFormat
      dates?: [Dayjs, Dayjs]
    }
    | undefined
  >()
  const [chartData, setChartData] = useState<ChartPt[]>([])
  const [mapStyle, setMapStyle] = useState<MapStyle>('Topo')
  const [chartZoom, setChartZoom] = useState<{ x: number; scale: number } | undefined>()
  const [_currentChartX, setCurrentChartX] = useState<number | undefined>()
  const [playSpeed, setPlaySpeed] = useState<PlaySpeed>(1)
  const {
    togglePlay,
    isPlaying,
    current: selectedTime,
    setCurrent: setSelectedTimeRaw,
  } = usePlayer({
    step: 5,
    stepUnit: 'minute',
    interval: 500 / playSpeed,
    timeRange: [
      timeRange?.[0] ?? getClosestFiveMin(dayjs()),
      timeRange?.[1] ?? getClosestFiveMin(dayjs()),
    ],
  })
  const selectedLayerRef = useRef(selectedLayer)
  const selectedTimeRef = useRef(selectedTime)
  const selectedStationRef = useRef(selectedStation)
  const stationsRef = useRef(stations)
  const obsMarkersRef = useRef(obsMarkers)
  const loadRunRef = useRef(0)

  const normalizeSelectedTimeForLayer = ({
    time,
    layer,
    markers,
  }: {
    time?: Dayjs
    layer: MapLayer
    markers?: ObsMarkerData[]
  }) => {
    const source = markers ?? obsMarkersRef.current ?? []
    const requested = time ? getClosestFiveMin(time) : undefined
    return resolveTimestampForLayer({
      markers: source,
      layer,
      requestedTime: requested,
    })
  }

  const updateObsMarkersShown = ({
    markers: newMarkers,
    time,
    layer,
    zoomLevel,
  }: {
    markers?: ObsMarkerData[]
    time?: Dayjs
    layer: MapLayer
    zoomLevel?: number
  }) => {
    setObsMarkers(({ obsMarkers }) => {
      const markers = newMarkers ?? obsMarkers ?? []
      const displayTime = normalizeSelectedTimeForLayer({
        time,
        layer,
        markers,
      })
      const msT = filterMarkersForTime({ markers, time: displayTime })
      const msG = groupMultiDevices(msT, layer)
      const msF = fillMissingStationMarkers({
        markers: msG,
        stations: stationsRef.current,
        layer,
        time: displayTime,
      })
      const msC = zoomLevel ? clusterMarkers(msF, layer, zoomLevel) : msF
      return { obsMarkersShown: msC, obsMarkers: markers }
    })
  }

  const buildStationChartData = ({
    stationId,
    layer,
    deviceId,
  }: {
    stationId?: string
    deviceId?: string
    layer?: MapLayer
  }) => {
    const id = stationId ?? selectedStationRef.current?.id
    const targetLayer = layer ?? selectedLayerRef.current

    setChartData(
      _(obsMarkersRef.current)
        ?.filter(v => v.id === id && (!deviceId || v.properties.deviceId === deviceId))
        .map(v => {
          const isForecast = v.properties.timestamp >= Date.now()
          const value = v.properties.obs[targetLayer]
          return {
            t: v.properties.timestamp,
            v: isForecast ? undefined : value,
            f: isForecast ? value : undefined,
          }
        })
        .filter(v => Number.isFinite(v.v) || Number.isFinite(v.f))
        .sortBy(v => v.t)
        .value() ?? [],
    )
  }

  const selectStation = ({
    stationId,
    layer,
    deviceId,
  }: {
    stationId?: string
    deviceId?: string
    layer?: MapLayer
  }) => {
    const shouldCancel = !stationId && !deviceId
    const id = stationId ?? selectedStationRef.current?.id
    const station = shouldCancel ? undefined : stations?.find(v => v.id === id) ?? selectedStationRef.current
    setSelectedStation(station)

    // If no deviceId specified, use the smallest device ID available for this station
    let targetDeviceId = deviceId
    if (!targetDeviceId && station && id) {
      const availableDeviceIds = _(obsMarkersRef.current)
        ?.filter(v => v.id === id)
        .map(v => v.properties.deviceId)
        .uniq()
        .sort()
        .value() ?? []
      targetDeviceId = availableDeviceIds[0]
    }

    buildStationChartData({
      stationId: id,
      layer,
      deviceId: targetDeviceId,
    })
  }

  const setSelectedTime = (time?: Dayjs) => {
    if (!time) return
    const normalized = normalizeSelectedTimeForLayer({
      time,
      layer: selectedLayerRef.current,
    })
    if (normalized) setSelectedTimeRaw(normalized)
  }

  const clickCtrBtn = (type: CtrBtnType | MapLayer, map?: MapRef) => {
    switch (type) {
      case 'zoom-in':
        return map?.zoomIn()
      case 'zoom-out':
        return map?.zoomOut()
      case 'direction':
        return map?.resetNorth()
      case 'locate':
        return navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude: lat, longitude: lng } }) => {
            flyToSmoothly({ map, lat, lng })
          },
        )
      case '3D':
        return setTiltDegree(v => {
          let newV = 0
          if (v < 30) newV = 30
          else if (v < 45) newV = 45
          else if (v < 60) newV = 60
          map?.setPitch(newV)
          return newV
        })
      default:
        const ly = type as MapLayer
        const nextTime = normalizeSelectedTimeForLayer({
          time: selectedTime,
          layer: ly,
        })
        setSelectLayer(ly)
        if (nextTime) {
          setSelectedTimeRaw(nextTime)
        }
        updateObsMarkersShown({
          time: nextTime,
          layer: ly,
          zoomLevel,
        })
        selectStation({ stationId: selectedStation?.id, layer: ly })
    }
  }

  useEffect(() => {
    selectedLayerRef.current = selectedLayer
  }, [selectedLayer])

  useEffect(() => {
    selectedTimeRef.current = selectedTime
  }, [selectedTime])

  useEffect(() => {
    selectedStationRef.current = selectedStation
  }, [selectedStation])

  useEffect(() => {
    stationsRef.current = stations
  }, [stations])

  useEffect(() => {
    obsMarkersRef.current = obsMarkers
  }, [obsMarkers])

  const onZoom = useCallback(debounce((z: number) => {
    setZoomLevel(z)
    updateObsMarkersShown({
      time: selectedTimeRef.current,
      layer: selectedLayerRef.current,
      zoomLevel: z,
    })
  }, 200), [])
  const onRotate = useCallback((d: number) => setRotateDegree(d), [setRotateDegree])
  const onTilt = useCallback((d: number) => setTiltDegree(d), [setTiltDegree])
  const goToDistrict = useCallback((districtId?: string | null, map?: MapRef) => {
    if (!isDistrict(districtId)) return
    const center = (DistrictLayerConfig[districtId] as {
      properties?: { lat?: number; lon?: number }
    })?.properties
    flyToSmoothly({
      map,
      lat: center?.lat,
      lng: center?.lon,
      zoom: 13,
    })
  }, [])

  const toggleDownload = () => {
    setDownloadSelection(v => (!v ? { stage: 'range' } : undefined))
  }

  const onDownloadSelectRange = (range: DownloadRange, dates?: [Dayjs, Dayjs]) => {
    const shouldGoToFormat = range !== 'custom' || dates
    const stage = shouldGoToFormat ? 'format' : 'range'
    setDownloadSelection({ stage, range, dates })
  }

  const getDownloadDates = (range?: DownloadRange): [Dayjs, Dayjs] | undefined => {
    switch (range) {
      case 'today':
        return [dayjs().startOf('day'), dayjs().endOf('day')]
      case 'yesterday':
        return [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')]
      case 'last-24h':
        return [dayjs().subtract(1, 'day'), dayjs()]
      case 'last-7d':
        return [dayjs().subtract(7, 'day'), dayjs()]
      default:
        return undefined
    }
  }

  const onDownload = async (format: DownloadFormat) => {
    toggleDownload()
    const dates = getDownloadDates(downloadSelection?.range) ?? downloadSelection?.dates
    const element = elmIdLookup[selectedLayer]
    if (dates && element) {
      const data = await downloadData({ element, dates, format })
      const filename = `${element}-${dates[0].format('YYYYMMDD')}-${dates[1].format(
        'YYYYMMDD',
      )}.${format}`
      download(data, filename, format === 'csv' ? 'text/csv' : 'application/json')
    }
  }

  const loadLayer = async (layer: MapLayer) => {
    const elmId = elmIdLookup[layer]
    const {
      metadata: { stations },
      items,
    } = await getObs(elmId)
    return [stations, items] as [Station[], ObsItem[]]
  }

  const loadLayerIntoMarkers = (
    layer: MapLayer,
    stations: Station[],
    items: ObsItem[],
    markers: ObsMarkerData[],
  ) => {
    const nextStations = uniqBy([...(stationsRef.current ?? []), ...stations], 'id')
    const nextMarkers = mergeMarkers(markers, items, nextStations, layer)

    return {
      markers: nextMarkers,
      stations: nextStations,
    }
  }

  const publishLoadedData = ({
    markers,
    stations,
    layer,
    zoom,
    updateTimeRange = true,
  }: {
    markers: ObsMarkerData[]
    stations: Station[]
    layer: MapLayer
    zoom: number
    updateTimeRange?: boolean
  }) => {
    stationsRef.current = stations
    setStations(stations)
    updateObsMarkersShown({
      markers,
      layer,
      zoomLevel: zoom,
    })

    if (updateTimeRange) {
      const maxTime = maxBy(markers, m => m.properties.timestamp)?.properties?.timestamp ??
        getClosestFiveMin(dayjs())
      const minTime = minBy(markers, m => m.properties.timestamp)?.properties?.timestamp ??
        dayjs(maxTime).subtract(1, 'day')
      setTimeRange([dayjs(minTime), dayjs(maxTime)] as [Dayjs, Dayjs])
    }

    const maxTime = maxBy(markers, m => m.properties.timestamp)?.properties?.timestamp ??
      getClosestFiveMin(dayjs())
    setSelectedTimeRaw(t => {
      const requested = t ?? dayjs(maxTime)
      return normalizeSelectedTimeForLayer({
        time: requested,
        layer: selectedLayerRef.current,
        markers,
      }) ?? requested
    })
  }

  const nextPlaySpeed = useCallback(() => {
    setPlaySpeed(v => playSpeeds[(playSpeeds.indexOf(v) + 1) % playSpeeds.length])
  }, [setPlaySpeed])

  const load = async () => {
    if (shouldSignIn()) {
      navigate('./login')
      return
    }

    const loadRunId = loadRunRef.current + 1
    loadRunRef.current = loadRunId

    const layers: MapLayer[] = ['temperature', 'humidity', 'pressure', 'wind', 'wind-direction']
    const [temperatureLayer, ...backgroundLayers] = layers
    let markers: ObsMarkerData[] = []
    let loadedStations: Station[] = []

    try {
      const [stns, itms] = await loadLayer(temperatureLayer)
      if (loadRunRef.current !== loadRunId) return
      const loaded = loadLayerIntoMarkers(temperatureLayer, stns, itms, markers)
      markers = loaded.markers
      loadedStations = loaded.stations
      publishLoadedData({
        markers,
        stations: loadedStations,
        layer: selectedLayerRef.current,
        zoom: zoomLevel ?? MapConfig.zoom,
        updateTimeRange: true,
      })
    } catch (error) {
    }

    await Promise.all(backgroundLayers.map(async layer => {
      try {
        const [stns, itms] = await loadLayer(layer)
        if (loadRunRef.current !== loadRunId) return

        const loaded = loadLayerIntoMarkers(layer, stns, itms, markers)
        markers = loaded.markers
        loadedStations = loaded.stations
        publishLoadedData({
          markers,
          stations: loadedStations,
          layer: selectedLayerRef.current,
          zoom: zoomLevel ?? MapConfig.zoom,
          updateTimeRange: false,
        })
      } catch (error) {
      }
    }))

    if (loadRunRef.current !== loadRunId) return
    publishLoadedData({
      markers,
      stations: loadedStations,
      layer: selectedLayerRef.current,
      zoom: zoomLevel ?? MapConfig.zoom,
      updateTimeRange: true,
    })
  }

  const zoomChartWhenFocus = (e: WheelEvent) => {
    if (_currentChartX === undefined) return
    e.preventDefault()

    setChartZoom(v => {
      const _scale = (v?.scale ?? 1) * (e.deltaY > 0 ? 0.8 : 1.2)
      return { x: _currentChartX, scale: clamp(_scale, 0.1, 1) }
    })
  }

  const goToLatest = () => {
    const latest = resolveTimestampForLayer({
      markers: obsMarkersRef.current ?? [],
      layer: selectedLayerRef.current,
    })
    const fallback = getClosestFiveMin(dayjs())
    setSelectedTimeRaw(latest ?? fallback)
  }

  const signOut = async () => {
    await signOutByApi()
    navigate('./login')
  }
  useEffect(() => {
    load()
    const timer = setInterval(() => {
      if (dayjs().minute() % 5 === 0) {
        load()
      }
    }, 60_000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    updateObsMarkersShown({
      time: selectedTime,
      layer: selectedLayer,
      zoomLevel,
    })
  }, [selectedTime])

  useEffect(() => {
    updateObsMarkersShown({
      time: selectedTime,
      layer: selectedLayer,
      zoomLevel,
    })
  }, [selectedLayer])

  useEffect(() => {
    if (!stations?.length) return
    updateObsMarkersShown({
      time: selectedTime,
      layer: selectedLayer,
      zoomLevel,
    })
  }, [stations])

  useEffect(() => {
    if (!selectedStationRef.current) return
    selectStation({ stationId: selectedStationRef.current.id, layer: selectedLayerRef.current })
  }, [obsMarkers])

  return {
    chartData,
    obsMarkersShown,
    selectedLayer,
    hoveringFeature,
    zoomLevel,
    rotateDegree,
    tiltDegree,
    selectedTime,
    selectedStation,
    mapStyle,
    timeRange,
    playSpeed,
    isPlaying,
    downloadSelection,
    chartZoom,
    setCurrentChartX,
    zoomChartWhenFocus,
    goToLatest,
    toggleDownload,
    onDownloadSelectRange,
    onDownload,
    togglePlay,
    setMapStyle,
    selectStation,
    setSelectedTime,
    onZoom,
    onRotate,
    onTilt,
    clickCtrBtn,
    setHoveringFeature,
    goToDistrict,
    nextPlaySpeed,
    signOut,
  }
}
